import { getCreateWith } from './createWith';
import { exportCanvasPng } from './exportCanvas';
import { canUndo, getRevision, lockCanvasInput, unlockCanvasInput } from '../libs/BitmapEditor/bitmapMaker3';
import type { CreateWith } from '../types/createWith';

// "Save while working": saves the artwork through the app without closing the
// editor (GUI route 11). Shared by the floating SAVE button and auto-save.

let lastSavedRevision: number | null = null;
let inFlight: Promise<void> | null = null;
const savedListeners = new Set<() => void>();
const savingListeners = new Set<() => void>();

/** Older store builds of the app don't offer `save`; fall back to save & close there. */
export const supportsSaveAndContinue = () => typeof getCreateWith()?.save === 'function';

/**
 * True when there is something new to save. Before the first save this needs a
 * real edit (canUndo), so opening a blank canvas never triggers a save.
 */
export const hasUnsavedChanges = () => (lastSavedRevision === null ? canUndo() : getRevision() !== lastSavedRevision);

export const isSaving = () => inFlight !== null;

/** Notified when a save starts or ends; the SAVE tab shows a spinner from it. */
export const onSavingChange = (listener: () => void) => {
	savingListeners.add(listener);
	return () => {
		savingListeners.delete(listener);
	};
};

/** Notified after every successful save; auto-save uses it to restart its timer. */
export const onSaved = (listener: () => void) => {
	savedListeners.add(listener);
	return () => {
		savedListeners.delete(listener);
	};
};

async function runSave(createWith: CreateWith, save: NonNullable<CreateWith['save']>, silent: boolean) {
	// A manual save shows the app's progress over the canvas, so block drawing
	// meanwhile. A silent save keeps drawing live: the image is taken here, and
	// anything drawn after this point stays marked as unsaved.
	if (!silent) lockCanvasInput();
	const revisionAtSave = getRevision();
	try {
		const result = await save.call(createWith, exportCanvasPng(), { silent });
		if (result?.skipped) return;
		lastSavedRevision = revisionAtSave;
		savedListeners.forEach(listener => listener());
	} finally {
		if (!silent) unlockCanvasInput();
	}
}

/**
 * Saves through the app without closing the editor. `silent` is for auto-save,
 * which must never interrupt the user. A manual save during a silent one waits
 * for it, then saves the latest image.
 */
export function saveAndContinue({ silent = false }: { silent?: boolean } = {}): Promise<void> {
	if (inFlight) {
		if (silent) return inFlight;
		return inFlight.catch(() => undefined).then(() => saveAndContinue());
	}
	const createWith = getCreateWith();
	if (!createWith?.save) return Promise.reject(new Error('Saving while drawing is not available in this version of the app.'));

	const notify = () => savingListeners.forEach(listener => listener());
	inFlight = runSave(createWith, createWith.save, silent).finally(() => {
		inFlight = null;
		notify();
	});
	notify();
	return inFlight;
}

// The save reminder can be switched off ("Don't show this message again").
// Stored in the WebView's localStorage, which persists between sessions.
const REMINDER_KEY = 'cwp.saveReminderDismissed';
export const isSaveReminderDismissed = () => {
	try {
		return window.localStorage.getItem(REMINDER_KEY) === '1';
	} catch {
		return false;
	}
};
export const dismissSaveReminder = () => {
	try {
		window.localStorage.setItem(REMINDER_KEY, '1');
	} catch {
		// Storage unavailable: the reminder simply shows again next time
	}
};
